import { Link } from "gatsby-link"
import pluralize from "pluralize"
import React from "react"
import {
    ExternalLink,
    Box,
    Text,
    SIZES,
    Avatar,
} from "@mallardbay/lib-react-components"
import type { StyleProps } from "@mallardbay/lib-react-components"

import { useCurrentListing } from "~components/providers/current-listing-provider"
import COPY from "~config/copy-constants"
import type { ListingType } from "~graphql/generated/graphql"
import { getListingTypeCamelcase } from "~utils/helpers"
import { SUPPORT_CONTACTS } from "~config/constants"
import { TEST_IDS } from "~config/test-ids"
import { isLodgingOnlyType } from "~utils/listing-helpers"

export default function ListingHost() {
    const { currentListing } = useCurrentListing()
    const styles = useStyles()

    const outfitter = currentListing.outfitter

    const hostText = getHostText({
        listingType: currentListing.type,
        outfitterName: outfitter.name,
    })
    const guestText = getGuestText({
        maxGuest: currentListing.max_guest || 0,
        minGuest: currentListing.min_guest || 0,
    })

    return (
        <Box style={styles.host}>
            <Box>
                <Box style={styles.hostName}>{hostText}</Box>
                <Box style={styles.tripSummary}>
                    <Text style={styles.guests}>{guestText}</Text>
                </Box>
            </Box>
            <Box style={styles.contactContainer}>
                <Link to={`/${outfitter.slug}`}>
                    <Avatar
                        testId={TEST_IDS.OUTFITTER_AVATAR}
                        src={outfitter.avatar?.url}
                        size={SIZES.LG}
                        name={outfitter.name}
                    />
                </Link>
                <ExternalLink
                    href={`mailto:${SUPPORT_CONTACTS.EMAIL}`}
                    isUnderlined
                >
                    {COPY.CONTACT_US}
                </ExternalLink>
            </Box>
        </Box>
    )
}

function getGuestText({
    maxGuest,
    minGuest,
}: {
    maxGuest: number
    minGuest: number
}) {
    const minimumGuestsText =
        minGuest > 1
            ? `, ${COPY.MINIMUM} ${minGuest} ${pluralize("guest", minGuest)}`
            : ""

    return `${COPY.UP_TO} ${maxGuest} ${COPY.GUESTS}${minimumGuestsText}`
}

function getHostText({
    listingType,
    outfitterName,
}: {
    listingType?: ListingType | null
    outfitterName: string
}) {
    const formattedListingType = getListingTypeCamelcase(listingType)

    if (isLodgingOnlyType(listingType)) {
        return `${COPY.LODGING_BY} ${outfitterName}`
    }

    return `${formattedListingType} ${COPY.ADVENTURE_GUIDED_BY} ${outfitterName}`
}

function useStyles() {
    const host: StyleProps = {
        display: "grid",
        gridTemplateColumns: "auto 100px",
        gridColumnGap: { base: "10px", md: "24px" },
        alignItems: "center",
    }

    const hostName: StyleProps = {
        fontWeight: 600,
        fontSize: { base: "17px", md: "21px" },
    }

    const tripSummary: StyleProps = {
        marginTop: "10px",
    }

    const guests: StyleProps = {
        fontWeight: 300,
    }

    const contactContainer: StyleProps = {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "15px",
    }

    return {
        host,
        hostName,
        tripSummary,
        guests,
        contactContainer,
    }
}
